/**
 * The one seam every Global Map frontend telemetry event goes through —
 * mirrors `apps/global_map/telemetry.py`'s `log_map_event` in spirit (denylist
 * guard, case-insensitive key match, bounded recursion into nested values) so
 * a sensitive value can't reach an event from either side of the wire.
 *
 * No generic named-event sink exists in this frontend yet: the only
 * `client-telemetry` channel (`src/observability/offline-transport-buffer.ts`,
 * batched to `/api/client-telemetry/`) is shaped for transport-failure
 * reporting (endpoint + retry count), not arbitrary product events like
 * `map_viewed`/`result_selected` — bending it into one is out of scope here.
 * `emitEvent` is the seam a real analytics sink replaces in a later increment.
 */

export type MapEventName =
  | "map_viewed"
  | "map_error"
  | "result_selected"
  | "details_opened"
  | "search_submitted"
  | "view_all_selected"
  | "search_no_results"
  | "search_error"

export type MapEventProps = Record<string, string | number>

// Same set `log_map_event` denylists, minus the backend's coordinate-shape
// check (this increment's event props are typed `string | number`, and every
// call site already buckets its counts/zooms into bands rather than passing a
// raw coordinate) plus `lat`/`lng`, the frontend's own shorthand for the same
// fields.
const DENYLISTED_KEYS = new Set([
  "title",
  "address",
  "query",
  "customer",
  "email",
  "lat",
  "lng",
  "latitude",
  "longitude"
])

// Deep enough for any structured prop this increment's events actually carry
// (they're flat by type); a defensive bound in case a future event nests a
// value, mirroring the backend's own `_MAX_NESTING_DEPTH`.
const MAX_SANITIZE_DEPTH = 4

const isDenylistedKey = (key: string): boolean =>
  DENYLISTED_KEYS.has(key.toLowerCase())

const sanitizeValue = (value: unknown, depth: number): unknown => {
  if (depth <= 0 || value === null || typeof value !== "object") return value

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, depth - 1))
  }

  const sanitized: Record<string, unknown> = {}
  for (const [key, nested] of Object.entries(
    value as Record<string, unknown>
  )) {
    if (isDenylistedKey(key)) continue
    sanitized[key] = sanitizeValue(nested, depth - 1)
  }
  return sanitized
}

/** Exported for its own unit tests; not part of the module's public event API. */
export const sanitizeMapEventProps = (props: MapEventProps): MapEventProps => {
  const safe: MapEventProps = {}
  for (const [key, value] of Object.entries(props)) {
    if (isDenylistedKey(key)) continue
    safe[key] = sanitizeValue(value, MAX_SANITIZE_DEPTH) as string | number
  }
  return safe
}

// TODO(increment-2): route to product analytics once this frontend has a
// generic event sink — see this file's docstring for why Increment 1 doesn't
// bend the existing client-telemetry batch channel into one.
const emitEvent = (event: string, props: MapEventProps): void => {
  if (import.meta.env.DEV) {
    console.debug(event, props)
  }
}

export const emitMapEvent = (
  name: MapEventName,
  props: MapEventProps
): void => {
  emitEvent(`global_map.${name}`, sanitizeMapEventProps(props))
}

/** Coarse magnitude, never the exact count. Mirrors the backend's `count_bucket`. */
export const countBucket = (n: number): string => {
  if (n <= 0) return "0"
  if (n <= 10) return "1-10"
  if (n <= 100) return "11-100"
  if (n <= 1000) return "101-1k"
  return "1k+"
}

/**
 * Map zoom (0-22) collapsed to the same coarse bands the client renders at.
 * Mirrors the backend's `zoom_bucket`.
 */
export const zoomBucket = (z: number): string => {
  if (z <= 3) return "world(0-3)"
  if (z <= 7) return "region(4-7)"
  if (z <= 11) return "city(8-11)"
  return "street(12+)"
}
